/**
 * D3/D4 — Assessment persistence tegen Supabase.
 *
 * Implementeert exact dezelfde signatures als de localStorage-variant in
 * `src/data/domains/assessment.ts`, zodat `USE_BACKEND` een echte schakelaar is
 * en niet een herschrijving van elke consumer.
 *
 * ─── TWEE CONTRACTEN DIE HIER OVEREIND MOETEN BLIJVEN ───────────────────────
 *
 * 1. **Het boolean-schrijfcontract.** `true` = duurzaam opgeslagen, `false` =
 *    niet opgeslagen. In de localStorage-versie was `false` een quota-fout; hier
 *    is het een HTTP- of RLS-fout. Een mislukte call MOET `false` teruggeven en
 *    mag NIET stil wegvallen in een `catch {}` — `completeAssessment()` hangt
 *    hiervan af om te beslissen of een teacher-assignment als afgerond mag
 *    worden gemarkeerd (AS-2026-07-P1-03).
 *
 * 2. **Learner-scoping.** In de localStorage-versie moest de aanroeper de juiste
 *    scoped accessor kiezen. Hier doet RLS het: een `select` geeft alleen rijen
 *    die `can_view_learner()` toestaat. "Alle attempts" betekent dus vanaf nu
 *    "alle attempts die ik mág zien" — een leerling ziet alleen zichzelf, een
 *    leerkracht zijn klas. Dat is een striktere garantie dan de client ooit kon
 *    geven, en hij geldt ook als iemand de frontend omzeilt.
 */

import { supabase, getCurrentUserId } from './client'
// `logger.ts` exporteert `error`; de codebase-conventie is de alias `logError`
// (zie o.a. `accounts/logic/relationships.ts`, `assessmentStorage.ts`).
import { error as logError } from '../../utils/logger'

/**
 * Bepaal wie de eigenaar is van een payload.
 *
 * Voorkeur voor het `learnerId`-veld op het object zelf (zo weten
 * `AssessmentAttempt` en `AssessmentReport` het al), met de ingelogde gebruiker
 * als fallback. Levert `null` als we het niet kunnen vaststellen — en dan
 * schrijven we NIET. Een rij zonder eigenaar is precies wat
 * `learner_id NOT NULL` in het schema uitsluit.
 */
async function resolveLearnerId(payload: unknown): Promise<string | null> {
  const candidate = (payload as { learnerId?: unknown } | null)?.learnerId
  if (typeof candidate === 'string' && candidate.length > 0) return candidate
  return getCurrentUserId()
}

/** Zet een rijenlijst om naar de `Record<id, payload>`-vorm die de app verwacht. */
function toRecord<T>(
  rows: Array<{ payload: unknown }> | null,
  keyOf: (row: Record<string, unknown>) => string,
): Record<string, T> {
  const out: Record<string, T> = {}
  for (const row of rows ?? []) {
    const key = keyOf(row as Record<string, unknown>)
    if (key) out[key] = (row as { payload: T }).payload
  }
  return out
}

// ── Blueprints ──────────────────────────────────────────────────────────────

export async function getAssessmentBlueprint<T = Record<string, unknown>>(
  id: string,
): Promise<T | null> {
  const { data, error } = await supabase
    .from('assessment_blueprints')
    .select('payload')
    .eq('id', id)
    .maybeSingle()
  if (error) {
    logError('[Supabase] getAssessmentBlueprint failed', error)
    return null
  }
  return (data?.payload as T) ?? null
}

export async function getAllAssessmentBlueprints<
  T = Record<string, unknown>,
>(): Promise<Record<string, T>> {
  const { data, error } = await supabase
    .from('assessment_blueprints')
    .select('id, payload')
  if (error) {
    logError('[Supabase] getAllAssessmentBlueprints failed', error)
    return {}
  }
  return toRecord<T>(data, (row) => row.id as string)
}

export async function saveAssessmentBlueprint<T = Record<string, unknown>>(
  id: string,
  blueprint: T,
): Promise<boolean> {
  const learnerId = await resolveLearnerId(blueprint)
  if (!learnerId) {
    logError('[Supabase] saveAssessmentBlueprint: geen learner-eigenaar bekend')
    return false
  }
  const { error } = await supabase
    .from('assessment_blueprints')
    .upsert({ id, learner_id: learnerId, payload: blueprint })
  if (error) {
    logError('[Supabase] saveAssessmentBlueprint failed', error)
    return false
  }
  return true
}

// ── Attempts ────────────────────────────────────────────────────────────────

export async function getAssessmentAttempt<T = Record<string, unknown>>(
  id: string,
): Promise<T | null> {
  const { data, error } = await supabase
    .from('assessment_attempts')
    .select('payload')
    .eq('id', id)
    .maybeSingle()
  if (error) {
    logError('[Supabase] getAssessmentAttempt failed', error)
    return null
  }
  return (data?.payload as T) ?? null
}

export async function getAllAssessmentAttempts<
  T = Record<string, unknown>,
>(): Promise<Record<string, T>> {
  const { data, error } = await supabase
    .from('assessment_attempts')
    .select('id, payload')
    .order('created_at', { ascending: false })
  if (error) {
    logError('[Supabase] getAllAssessmentAttempts failed', error)
    return {}
  }
  return toRecord<T>(data, (row) => row.id as string)
}

/**
 * Schrijf een attempt weg.
 *
 * De losse kolommen (`status`, `domain_id`, `completed_at`, …) worden uit de
 * payload gelicht zodat de database erop kan filteren en indexeren; de payload
 * blijft de volledige waarheid. Loopt dat uiteen, dan is de payload leidend.
 */
export async function saveAssessmentAttempt<T = Record<string, unknown>>(
  id: string,
  attempt: T,
): Promise<boolean> {
  const learnerId = await resolveLearnerId(attempt)
  if (!learnerId) {
    logError('[Supabase] saveAssessmentAttempt: geen learner-eigenaar bekend')
    return false
  }
  const a = attempt as Record<string, unknown>
  const completedAt = typeof a.completedAt === 'number' ? a.completedAt : null

  const { error } = await supabase.from('assessment_attempts').upsert({
    id,
    learner_id: learnerId,
    blueprint_id: typeof a.blueprintId === 'string' ? a.blueprintId : null,
    domain_id: typeof a.domainId === 'string' ? a.domainId : null,
    selected_group:
      typeof a.selectedGroup === 'number' ? a.selectedGroup : null,
    status: typeof a.status === 'string' ? a.status : 'in_progress',
    assignment_id: typeof a.assignmentId === 'string' ? a.assignmentId : null,
    completed_at: completedAt ? new Date(completedAt).toISOString() : null,
    payload: attempt,
    updated_at: new Date().toISOString(),
  })
  if (error) {
    logError('[Supabase] saveAssessmentAttempt failed', error)
    return false
  }
  return true
}

// ── Reports ─────────────────────────────────────────────────────────────────

export async function getAssessmentReport<T = Record<string, unknown>>(
  attemptId: string,
): Promise<T | null> {
  const { data, error } = await supabase
    .from('assessment_reports')
    .select('payload')
    .eq('attempt_id', attemptId)
    .maybeSingle()
  if (error) {
    logError('[Supabase] getAssessmentReport failed', error)
    return null
  }
  return (data?.payload as T) ?? null
}

export async function getAllAssessmentReports<
  T = Record<string, unknown>,
>(): Promise<Record<string, T>> {
  const { data, error } = await supabase
    .from('assessment_reports')
    .select('attempt_id, payload')
  if (error) {
    logError('[Supabase] getAllAssessmentReports failed', error)
    return {}
  }
  return toRecord<T>(data, (row) => row.attempt_id as string)
}

export async function saveAssessmentReport<T = Record<string, unknown>>(
  attemptId: string,
  report: T,
): Promise<boolean> {
  const learnerId = await resolveLearnerId(report)
  if (!learnerId) {
    logError('[Supabase] saveAssessmentReport: geen learner-eigenaar bekend')
    return false
  }
  const { error } = await supabase
    .from('assessment_reports')
    .upsert({ attempt_id: attemptId, learner_id: learnerId, payload: report })
  if (error) {
    logError('[Supabase] saveAssessmentReport failed', error)
    return false
  }
  return true
}

// ── CRUD helpers ────────────────────────────────────────────────────────────

/**
 * Verwijderen is admin-only volgens de policies. Voor een gewone gebruiker
 * faalt dit stil aan de databasekant (0 rijen geraakt) — dat is bedoeld: een
 * leerling mag eigen resultaten niet wissen.
 */
export async function deleteAssessmentAttempt(id: string): Promise<void> {
  // Het report gaat mee via ON DELETE CASCADE op attempt_id.
  const { error } = await supabase
    .from('assessment_attempts')
    .delete()
    .eq('id', id)
  if (error) logError('[Supabase] deleteAssessmentAttempt failed', error)
}

/**
 * Bewust NIET geïmplementeerd tegen de backend.
 *
 * In localStorage was dit "wis mijn eigen apparaat". Tegen een gedeelde database
 * zou het "wis toetsdata" betekenen — een destructieve actie die per ongeluk een
 * hele klas kan raken. Dat hoort een expliciete, geauditeerde admin-operatie te
 * zijn, geen dataLayer-aanroep die een component kan doen.
 */
export async function clearAllAssessmentData(): Promise<void> {
  logError(
    '[Supabase] clearAllAssessmentData is in backend-modus opzettelijk niet beschikbaar. Gebruik een admin-tool.',
  )
}

// ── D4: Session recovery ────────────────────────────────────────────────────
//
// Eén rij per leerling (learner_id is primary key). De eigendomscheck uit
// AS-2026-07-P1-02 is hier structureel: je kunt de sessie van een ander niet
// eens opvragen, want de policy laat alleen `learner_id = auth.uid()` door.

export async function getAssessmentSession<
  T = Record<string, unknown>,
>(): Promise<T | null> {
  const userId = await getCurrentUserId()
  if (!userId) return null
  const { data, error } = await supabase
    .from('assessment_sessions')
    .select('payload')
    .eq('learner_id', userId)
    .maybeSingle()
  if (error) {
    logError('[Supabase] getAssessmentSession failed', error)
    return null
  }
  return (data?.payload as T) ?? null
}

export async function saveAssessmentSession<T = Record<string, unknown>>(
  session: T,
): Promise<boolean> {
  const userId = await getCurrentUserId()
  if (!userId) return false
  const { error } = await supabase.from('assessment_sessions').upsert({
    learner_id: userId,
    payload: session,
    saved_at: new Date().toISOString(),
  })
  if (error) {
    logError('[Supabase] saveAssessmentSession failed', error)
    return false
  }
  return true
}

export async function clearAssessmentSession(): Promise<void> {
  const userId = await getCurrentUserId()
  if (!userId) return
  const { error } = await supabase
    .from('assessment_sessions')
    .delete()
    .eq('learner_id', userId)
  if (error) logError('[Supabase] clearAssessmentSession failed', error)
}
